import * as React from "react";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { I } from "../shared/icons/icons.tsx";
import {
  diagnosticForPatient,
  type DiagnosticData,
} from "./clinical-snippets.ts";
import {
  RichTextEditor,
  RichTextDisplay,
} from "../shared/rich-text-editor.component.tsx";

function buildHtml(d: DiagnosticData): string {
  return `
<h2>Diagnostic principal</h2>
<p><strong>Classification :</strong> ${d.classification}</p>
<h3>Bilan squelettique</h3>
<p>${d.skeletalRelation}</p>
<h3>Bilan dentaire</h3>
<p>${d.dentalRelation}</p>
<p><strong>Encombrement :</strong> ${d.crowding}</p>
<p><strong>Dimension verticale :</strong> ${d.verticalDimension}</p>
<p><strong>Relation transversale :</strong> ${d.transverseRelation}</p>
<hr/>
<h3>Observations</h3>
<p>${d.notes}</p>
`.trim();
}

export function DiagnosticWidget() {
  const { current } = Interactions.usePatient();
  const toast = Interactions.useToast();

  const editsRef = React.useRef<Map<string, string>>(new Map());
  const [editing, setEditing] = React.useState(false);
  const [, force] = React.useReducer((x: number) => x + 1, 0);

  const seedHtml = React.useMemo(
    () => (current ? buildHtml(diagnosticForPatient(current.uuid)) : ""),
    [current?.uuid],
  );

  React.useEffect(() => {
    setEditing(false);
  }, [current?.uuid]);

  if (!current) {
    return (
      <div className="flex h-full min-h-24 flex-col items-center justify-center rounded-xl border border-dashed border-sf-border py-8 text-center">
        <I.Stethoscope size={20} className="mb-2 text-sf-text-muted" />
        <p className="text-[12.5px] text-sf-text-muted">
          Sélectionnez un patient
        </p>
      </div>
    );
  }

  const html = editsRef.current.get(current.uuid) ?? seedHtml;

  const handleSave = (newHtml: string) => {
    editsRef.current.set(current.uuid, newHtml);
    setEditing(false);
    force();
    toast.push({ type: "success", title: "Diagnostic enregistré" });
  };

  if (editing) {
    return (
      <RichTextEditor
        defaultHtml={html}
        placeholder="Saisir le diagnostic…"
        onSave={handleSave}
        onCancel={() => setEditing(false)}
        className="h-full"
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider text-sf-text-muted">
          <I.Stethoscope size={12} />
          Diagnostic
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex h-7 w-7 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-gray-50"
          title="Modifier"
        >
          <I.Edit size={13} />
        </button>
      </div>
      <div className="rounded-lg border border-sf-border bg-white p-2.5">
        <RichTextDisplay html={html} />
      </div>
    </div>
  );
}
